import React from 'react';

/**
 * UnifiedLoadingState — canonical "we're fetching" placeholder.
 *
 * Prior state: every page ran its own animate-spin block with a slightly
 * different border color, size (h-8 / h-10 / h-12), or wrapper style.
 * This primitive replaces those with one call site so the "we're
 * fetching" moment feels identical across the platform.
 *
 * Two rendering modes:
 *  - spinner (default)  → gold spinner + optional label, framed like a card
 *  - inline              → small spinner, no card frame (for use inside cards)
 *
 * Props:
 *  - label: optional loading message
 *  - variant: 'card' | 'inline'
 *  - size: 'sm' | 'md' | 'lg'
 *  - testId
 */
export const UnifiedLoadingState = ({
  label,
  variant = 'card',
  size = 'md',
  className = '',
  testId,
}) => {
  const dims = { sm: 20, md: 32, lg: 44 }[size];
  const borderWidth = size === 'sm' ? 2 : 3;

  const spinner = (
    <div
      role="status"
      aria-live="polite"
      style={{
        width: dims,
        height: dims,
        border: `${borderWidth}px solid var(--mesp-border)`,
        borderTopColor: 'var(--mesp-gold)',
        borderRadius: '50%',
        animation: 'mesp-spin 900ms linear infinite',
      }}
    />
  );

  if (variant === 'inline') {
    return (
      <div
        className={`mesp-ui inline-flex items-center gap-2 ${className}`}
        data-testid={testId}
        style={{ color: 'var(--mesp-text-muted)' }}
      >
        {spinner}
        {label && (
          <span style={{ fontSize: 'var(--mesp-font-sm)' }}>{label}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`mesp-ui ${className}`}
      data-testid={testId}
      style={{
        background: 'var(--mesp-bg-elevated)',
        border: '1px solid var(--mesp-border)',
        borderRadius: 'var(--mesp-radius-lg)',
        padding: 'var(--mesp-space-10) var(--mesp-space-6)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--mesp-space-3)',
        color: 'var(--mesp-text-muted)',
      }}
    >
      {spinner}
      {label && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--mesp-font-sm)',
            color: 'var(--mesp-text-muted)',
          }}
        >
          {label}
        </p>
      )}
    </div>
  );
};

export default UnifiedLoadingState;
